// @ts-nocheck
import React, { useRef, useState } from "react";
import { format } from "date-fns";

import AttachFileIcon from "@material-ui/icons/AttachFile";
import CheckIcon from "@material-ui/icons/Check";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import { isPlano2026, taxaConservatoria } from "../../../helpers/process.helper";

import {
  Corpo,
  Selo,
  Titulo,
  Aviso,
  Blocos,
  Bloco,
  Progresso,
  BotaoLink,
  BotaoArquivo,
  Secao,
  Itens,
  Item,
  Etiqueta,
  Ajuda,
  Arquivo,
  diaMes,
  diasDaEtapa,
  linkWhatsApp,
} from "./ui";

const ACEITA_COMPROVANTE = ".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/*";
const TAMANHO_MAXIMO_MB = 10;

// Despacho (post). Plano 2026: a taxa da conservatória é do cliente — paga
// pelo link (PayPal, por tipo de processo) e anexa o comprovante, que a
// equipe confere no envio; sem link (homologações) acerta pelo WhatsApp;
// menor de idade não paga. Sem dado por item da preparação: a barra conta
// os dias até a previsão de envio (decidido em 29/10/2026).
export default function Despacho({ process }) {
  const { addToast } = useToast();
  const inputRef = useRef(null);
  const plano2026 = isPlano2026(process?.sale?.family_plan);
  const taxa = plano2026 ? taxaConservatoria(process?.type) : { modo: "nenhum" };
  const [comprovante, setComprovante] = useState(process?.postal_fee_receipt || null);
  const [enviando, setEnviando] = useState(false);

  const numeroPedido = process?.service_code || process?.sale?.client_id || "";
  const requerente = process?.applicant?.name || "";
  const linkEquipe = linkWhatsApp(
    `Olá! Meu processo ${numeroPedido}${requerente ? ` (${requerente})` : ""} está na etapa de despacho e quero pagar a taxa da conservatória. Podem me orientar sobre o valor e a forma de pagamento?`
  );

  const enviarComprovante = async (event) => {
    const arquivo = event.target.files && event.target.files[0];
    if (!arquivo) return;
    if (arquivo.size > TAMANHO_MAXIMO_MB * 1024 * 1024) {
      addToast({ type: "error", title: "Arquivo muito grande", description: `Envie um comprovante de até ${TAMANHO_MAXIMO_MB} MB.` });
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    const formData = new FormData();
    formData.append("file", arquivo);
    setEnviando(true);
    try {
      const response = await api.post(`central/processes/${process?.id}/postal-fee-receipt`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const atualizado = response?.data?.data || response?.data || {};
      setComprovante(atualizado?.postal_fee_receipt || null);
      addToast({ type: "success", title: "Comprovante recebido", description: "Vamos conferir o pagamento no momento do envio do seu processo." });
    } catch (err) {
      addToast({
        type: "error",
        title: "Não foi possível anexar",
        description: err?.response?.data?.message || "Tente de novo em instantes. Se continuar, fale com o nosso suporte.",
      });
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const botaoComprovante = (secundario) => (
    <BotaoArquivo secundario={secundario} aria-disabled={enviando}>
      <AttachFileIcon />
      {enviando ? "Enviando…" : comprovante ? "Trocar comprovante" : "Anexar comprovante"}
      <input ref={inputRef} type="file" accept={ACEITA_COMPROVANTE} disabled={enviando} onChange={enviarComprovante} />
    </BotaoArquivo>
  );

  const vezDoCliente = (taxa.modo === "link" && !comprovante) || taxa.modo === "equipe";
  const dias = diasDaEtapa(process?.ready_to_post_date, process?.post_prediction_date);
  const previsaoEnvio = diaMes(process?.post_prediction_date);
  const atrasou = !!process?.reanalysis_date && dias?.atrasado;
  const temTaxa = taxa.modo === "link" || taxa.modo === "equipe";

  let titulo;
  let frase;
  if (taxa.modo === "link" && !comprovante) {
    titulo = "Pague a taxa da conservatória";
    frase = "Estamos montando o processo para enviar a Portugal. Falta só a taxa da conservatória: pague pelo botão e anexe o comprovante aqui.";
  } else if (taxa.modo === "link") {
    titulo = "Comprovante recebido";
    frase = "Vamos conferir o pagamento no momento do envio do seu processo. Nada a fazer agora.";
  } else if (taxa.modo === "equipe") {
    titulo = "Combine a taxa da conservatória com a equipe";
    frase = "Estamos montando o processo para enviar a Portugal. Neste tipo de pedido, a taxa é acertada com a nossa equipe: chame a gente no WhatsApp para receber os dados do pagamento.";
  } else {
    titulo = "Estamos preparando o envio a Portugal";
    frase = "Montamos o processo com todos os documentos e o requerimento. Nada a fazer agora.";
  }

  return (
    <Corpo>
      <Selo tipo={vezDoCliente ? "cliente" : "equipe"} />
      <Titulo>{titulo}</Titulo>
      <p className="desc">{frase}</p>

      <Blocos>
        {temTaxa ? (
          <Bloco rotulo="Taxa da conservatória">{taxa.modo === "equipe" ? "Combinada com a equipe" : comprovante ? "Comprovante enviado" : "Paga por você"}</Bloco>
        ) : (
          <Bloco rotulo="Quanto demora">Até 10 dias</Bloco>
        )}
        <Bloco rotulo={temTaxa && !comprovante ? "Depois da taxa" : "Envio previsto"}>
          {temTaxa && !comprovante ? "Enviamos em até 10 dias" : previsaoEnvio ? `Até ${previsaoEnvio}` : "Enviamos a Portugal"}
        </Bloco>
      </Blocos>

      {dias && !vezDoCliente && <Progresso rotulo={`Preparação do envio · dia ${Math.min(dias.atual, dias.total)} de ${dias.total}`} percentual={dias.percentual} />}

      {atrasou && (
        <Aviso tipo="cliente">
          <b>O despacho atrasou um pouco</b>
          <p>O operador logístico apresentou um novo prazo para coleta do seu malote.</p>
        </Aviso>
      )}

      {taxa.modo === "link" && !comprovante && (
        <>
          <Aviso tipo="neutro">
            <p>Na descrição do pagamento, informe o nome do requerente e o número do pedido:</p>
            <b>
              {requerente}
              {requerente && numeroPedido ? " — " : ""}
              {numeroPedido}
            </b>
          </Aviso>
          <BotaoLink href={taxa.link} target="_blank" rel="noreferrer">
            <OpenInNewIcon />
            Pagar a taxa da conservatória
          </BotaoLink>
          {botaoComprovante(true)}
        </>
      )}

      {taxa.modo === "link" && comprovante && (
        <>
          <Arquivo href={comprovante?.url || undefined} target="_blank" rel="noreferrer">
            <span className="ic">
              <InsertDriveFileOutlinedIcon />
            </span>
            <div>
              <b>{comprovante?.name || "Comprovante da taxa"}</b>
              {comprovante?.uploaded_at && <small>Enviado em {format(new Date(comprovante.uploaded_at), "dd/MM 'às' HH:mm")}</small>}
            </div>
          </Arquivo>
          {botaoComprovante(true)}
        </>
      )}

      {taxa.modo === "equipe" && (
        <BotaoLink href={linkEquipe} target="_blank" rel="noreferrer">
          <WhatsAppIcon />
          Falar com a equipe no WhatsApp
        </BotaoLink>
      )}

      <Secao>Preparação para envio</Secao>
      <Itens>
        {temTaxa && (
          <Item
            tipo={vezDoCliente ? "cliente" : "ok"}
            icone={vezDoCliente ? <CreditCardIcon /> : <CheckIcon />}
            titulo="Taxa da conservatória"
            descricao={
              taxa.modo === "equipe"
                ? "Acertada com a equipe pelo WhatsApp"
                : comprovante
                ? "Comprovante recebido, validando no envio"
                : "Feito por você, pelo botão acima"
            }
          >
            {vezDoCliente && <Etiqueta tipo="cliente">Sua vez</Etiqueta>}
          </Item>
        )}
        <Item tipo="ok" icone={<CheckIcon />} titulo="Todos os documentos reunidos" descricao="Conferidos e apostilados" />
        <Item tipo="neutro" icone={<EditOutlinedIcon />} titulo="Requerimento preenchido e anexado" descricao="A nossa equipe prepara e anexa" />
      </Itens>

      <Ajuda
        titulo="Depois do envio"
        passos={[
          "O código de rastreio aparece aqui no card.",
          process?.type === "Transcrição de Casamento"
            ? "Acompanhamos o pedido na conservatória e avisamos aqui quando houver novidade."
            : "Quando a conservatória registrar o pedido, mostramos a senha de acesso para você consultar no site da Justiça.",
        ]}
      />
    </Corpo>
  );
}
